/**
 * Puerto de notificaciones
 *
 * Abstrae las notificaciones al usuario para permitir diferentes implementaciones:
 * - Web: Vue Toastification, custom toasts
 * - Mobile: Ionic Toast, Native notifications
 */

export type NotificationType = 'success' | 'error' | 'warning' | 'info'

export interface NotificationOptions {
  /** Mensaje a mostrar */
  message: string
  /** Tipo de notificación */
  type?: NotificationType
  /** Título opcional */
  title?: string
  /** Duración en milisegundos (0 = permanente) */
  duration?: number
  /** Si la notificación puede cerrarse manualmente */
  dismissible?: boolean
  /** Posición en pantalla */
  position?: 'top' | 'bottom' | 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'
  /** Acción al hacer clic */
  onClick?: () => void
}

export interface NotificationPort {
  /**
   * Muestra una notificación genérica
   */
  show(options: NotificationOptions): void

  /**
   * Muestra una notificación de éxito
   */
  success(message: string, title?: string): void

  /**
   * Muestra una notificación de error
   */
  error(message: string, title?: string): void

  /**
   * Muestra una notificación de advertencia
   */
  warning(message: string, title?: string): void

  /**
   * Muestra una notificación informativa
   */
  info(message: string, title?: string): void

  /**
   * Cierra todas las notificaciones activas
   */
  dismissAll(): void
}

// ============== REGISTRO GLOBAL ==============

let notificationInstance: NotificationPort | null = null

/**
 * Registra la implementación de NotificationPort
 */
export function registerNotification(notification: NotificationPort): void {
  notificationInstance = notification
}

/**
 * Obtiene la instancia registrada de NotificationPort
 */
export function getNotification(): NotificationPort {
  if (!notificationInstance) {
    throw new Error('NotificationPort no registrado. Llama a registerNotification() primero.')
  }
  return notificationInstance
}

/**
 * Verifica si hay un NotificationPort registrado
 */
export function isNotificationRegistered(): boolean {
  return notificationInstance !== null
}

/**
 * Resetea el registro (útil para tests)
 */
export function resetNotification(): void {
  notificationInstance = null
}
